import Vue from "vue";
import VueRouter from "vue-router";
import store from "../store";

Vue.use(VueRouter);

function isTokenValid() {
    const token = localStorage.getItem("authToken");
    if (!token) return false;
    try {
        const parts = token.split(".");
        if (parts.length !== 3) return false;
        const payload = JSON.parse(atob(parts[1]));
        if (payload.exp && (payload.exp * 1000) <= Date.now()) {
            return false;
        }
        return true;
    } catch (e) {
        return false;
    }
}

function hasSession() {
    return isTokenValid() || Boolean(localStorage.getItem("refreshToken"));
}

const guest = (to, from, next) => {
    if (!hasSession()) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("integrador");
        store.commit("auth/setUserData", null);
        return next();
    } else {
        const user = store.getters["auth/user"] || getUserFromToken();
        if (user && user.rol !== "admin" && user.integradores && user.integradores.length === 0) {
            localStorage.removeItem("authToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("integrador");
            store.commit("auth/setUserData", null);
            store.commit("setError", "El usuario no posee ningún integrador asignado", { root: true });
            return next();
        }
        let integrador = localStorage.getItem("integrador");
        if (!integrador && user && user.integradores && user.integradores.length > 0) {
            integrador = user.integradores[0].id_integrador || user.integradores[0].id;
            localStorage.setItem("integrador", integrador);
            store.commit("integrador/setIntegrador", integrador);
        }
        return next(getDefaultRouteForUser(user));
    }
};

const auth = (to, from, next) => {
    if (hasSession()) {
        const user = store.getters["auth/user"] || getUserFromToken();
        if (user && user.rol !== "admin" && user.integradores && user.integradores.length === 0) {
            localStorage.removeItem("authToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("integrador");
            store.commit("auth/setUserData", null);
            store.commit("setError", "El usuario no posee ningún integrador asignado", { root: true });
            return next("/login");
        }
        let integrador = localStorage.getItem("integrador");
        if (!integrador && user && user.integradores && user.integradores.length > 0) {
            integrador = user.integradores[0].id_integrador || user.integradores[0].id;
            localStorage.setItem("integrador", integrador);
            store.commit("integrador/setIntegrador", integrador);
        }
        return next();
    } else {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("integrador");
        store.commit("auth/setUserData", null);
        return next("/login");
    }
};

export function getUserFromToken() {
    const token = localStorage.getItem("authToken");
    if (!token) return null;
    try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        return JSON.parse(atob(parts[1]));
    } catch (e) {
        return null;
    }
}

export function userHasPermission(user, permission) {
    if (!user) return false;
    const perms = user.permissions || [];
    if (Array.isArray(perms)) {
        const cleaned = perms.map(p => String(p).trim().toUpperCase());
        if (cleaned.includes("*") || cleaned.includes("ALL")) return true;
        return cleaned.includes(permission.toUpperCase());
    }
    return false;
}

export function getDefaultRouteForUser(user) {
    if (!user) return "/login";
    const isAdmin = user.rol === "admin";

    // 1. Dashboard es la primera opción si tiene el permiso
    if (userHasPermission(user, "GEN_DASHBOARD")) {
        return "/";
    }

    // 2. Primera opción disponible según el orden del menú
    if (isAdmin && userHasPermission(user, "GEN_OPERADOR")) {
        return "/operadores";
    }
    if (isAdmin && userHasPermission(user, "GEN_INTEGRADOR")) {
        return "/integradores";
    }
    if (userHasPermission(user, "GEN_KEYWORD")) {
        return "/keywords";
    }
    if (userHasPermission(user, "GEN_PRODUCTO")) {
        return "/productos";
    }
    if (userHasPermission(user, "GEN_SERVICIO")) {
        return "/servicios";
    }
    if (userHasPermission(user, "GEN_BILLING")) {
        return "/billingcodes";
    }
    if (isAdmin && userHasPermission(user, "GEN_USUARIO")) {
        return "/usuarios";
    }
    if (userHasPermission(user, "GEN_REPORTE")) {
        return "/tareas";
    }

    // 3. Fallback a perfil de usuario
    return "/perfil";
}

const routes = [{
        path: "/",
        name: "Home",
        beforeEnter: auth,
        component: () => import("../views/components/Dashboard.vue"),
        props: true,
        meta: { permission: "GEN_DASHBOARD" }
    },
    {
        path: "/selectintegrador",
        name: "selectIntegrador",
        beforeEnter: (to, from, next) => {
            const user = store.getters["auth/user"] || getUserFromToken();
            let integrador = localStorage.getItem("integrador");
            if (!integrador && user && user.integradores && user.integradores.length > 0) {
                integrador = user.integradores[0].id_integrador || user.integradores[0].id;
                localStorage.setItem("integrador", integrador);
                store.commit("integrador/setIntegrador", integrador);
            }
            if (integrador) {
                return next(getDefaultRouteForUser(user));
            }
            next();
        },
        component: () => import("../views/components/selectIntegrador.vue"),
        props: true
    },
    {
        path: "/dashboard",
        name: "Dashboard",
        beforeEnter: auth,
        component: () => import("../views/components/Dashboard.vue"),
        props: true,
        meta: { permission: "GEN_DASHBOARD" }
    },
    {
        path: "/atributos",
        name: "Atributos",
        beforeEnter: auth,
        component: () => import("../views/components/Atributos.vue"),
        props: true
    },
    {
        path: "/operadores",
        name: "Operadores",
        beforeEnter: auth,
        component: () => import("../views/components/Operadores.vue"),
        meta: { permission: "GEN_OPERADOR", adminOnly: true }
    },
    {
        path: "/integradores",
        name: "Integradores",
        beforeEnter: auth,
        component: () => import("../views/components/Integradores.vue"),
        meta: { permission: "GEN_INTEGRADOR", adminOnly: true }
    },
    {
        path: "/keywords",
        name: "Keywords",
        beforeEnter: auth,
        component: () => import("../views/components/Keywords.vue"),
        meta: { permission: "GEN_KEYWORD" }
    },
    {
        path: "/productos",
        name: "Productos",
        beforeEnter: auth,
        component: () => import("../views/components/Productos.vue"),
        meta: { permission: "GEN_PRODUCTO" }
    },
    {
        path: "/servicios",
        name: "Servicios",
        beforeEnter: auth,
        component: () => import("../views/components/Servicios.vue"),
        meta: { permission: "GEN_SERVICIO" }
    },
    {
        path: "/billingcodes",
        name: "BillingCodes",
        beforeEnter: auth,
        component: () => import("../views/components/BillingCodes.vue"),
        meta: { permission: "GEN_BILLING" }
    },
    {
        path: "/billingsequences",
        name: "BillingSequences",
        beforeEnter: auth,
        component: () => import("../views/components/BillingSequences.vue"),
        meta: { permission: "GEN_BILLING" }
    },
    {
        path: "/tareas",
        name: "Tareas",
        beforeEnter: auth,
        component: () => import("../views/components/Tareas.vue"),
        meta: { permission: "GEN_REPORTE" }
    },
    {
        path: "/usuarios",
        name: "Usuarios",
        beforeEnter: auth,
        component: () => import("../views/components/Usuarios.vue"),
        meta: { permission: "GEN_USUARIO", adminOnly: true }
    },
    {
        path: "/perfil",
        name: "Perfil",
        beforeEnter: auth,
        component: () => import("../views/components/Perfil.vue")
    },
    {
        path: "/login",
        name: "Login",
        beforeEnter: guest,
        component: () => import("../views/Auth/Login.vue")
    }

];

const router = new VueRouter({
    mode: "history",
    base: process.env.BASE_URL,
    routes
});

router.beforeEach((to, from, next) => {
    if (to.name !== "Login") {
        if (!hasSession()) {
            localStorage.removeItem("authToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("integrador");
            store.commit("auth/setUserData", null);
            store.commit("resetLoading");
            return next({ name: "Login" });
        }
        const user = store.getters["auth/user"] || getUserFromToken();
        if (user && user.rol !== "admin" && user.integradores && user.integradores.length === 0) {
            localStorage.removeItem("authToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("integrador");
            store.commit("auth/setUserData", null);
            store.commit("resetLoading");
            store.commit("setError", "El usuario no posee ningún integrador asignado", { root: true });
            return next({ name: "Login" });
        }
        if (user && to.meta) {
            if (to.meta.adminOnly && user.rol !== "admin") {
                const target = getDefaultRouteForUser(user);
                if (to.path !== target) {
                    return next(target);
                }
                return next("/perfil");
            }
            if (to.meta.permission && !userHasPermission(user, to.meta.permission)) {
                const target = getDefaultRouteForUser(user);
                if (to.path !== target) {
                    return next(target);
                }
                return next("/perfil");
            }
        }
    }
    next();
});

export default router;